import { useCallback, useMemo, useState } from 'react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import Spinner from '~components/ui/loading-indicator/spinner'
import Consultation from '~models/consultation'
import useRelatedConsultations from '~repositories/hooks/use-related-consultations'
import { cn } from '~utils/cn'
import Mixpanel from '~utils/mixpanel'
import Header from './header'
import SmartSearchBottomSheet from './smart-search-bottom-sheet'
import SmartSearchCard from './smart-search-card'
import SmartSearchEmptyCase from './smart-search-empty-case'
import SmartSearchError from './smart-search-error'

type Props = {
  id: number | string
}

type ClickedQuestion = {
  id: number
  match_rate: Consultation.Level1['match_rate']
}

const bulletCss =
  '[&_.swiper-pagination-bullet]:size-2 [&_.swiper-pagination-bullet]:bg-gray-400 [&_.swiper-pagination-bullets]:flex [&_.swiper-pagination-bullets]:items-center [&_.swiper-pagination-bullets]:justify-center [&_.swiper-pagination-bullets]:flex-gap-x-2 [&_.swiper-pagination-bullet-active]:!bg-secondary-500 [&_.swiper-pagination-bullet]:!m-0 [&_.swiper-pagination]:!bottom-4 dark:[&_.swiper-pagination-bullet]:bg-gray-800!'

const getMatchRate = (matchRate: Consultation.Level1['match_rate']) =>
  Math.round((matchRate || 0) * 100)

export const SmartSearch = ({ id }: Readonly<Props>) => {
  const { data: relatedConsultations, isError, isFetching, refetch } = useRelatedConsultations(id)
  const [clickedQuestion, setClickedQuestion] = useState<ClickedQuestion | null>(null)

  const resetClickedQuestion = useCallback(() => setClickedQuestion(null), [])

  const contentsProps = useMemo(
    () => ({
      id: clickedQuestion?.id || 0,
      matchRate: clickedQuestion?.match_rate || 0,
      onClose: resetClickedQuestion,
    }),
    [clickedQuestion?.id, clickedQuestion?.match_rate, resetClickedQuestion]
  )

  if (isError) {
    return <SmartSearchError onRetry={refetch} />
  }

  if (isFetching) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      </>
    )
  }

  if (!isFetching && !relatedConsultations?.length) {
    return <SmartSearchEmptyCase />
  }

  return (
    <>
      <Header />
      <div className={cn('-mx-5 mt-4 lg:mx-0 lg:mt-5 pb-16', bulletCss)}>
        <Swiper
          pagination={{ clickable: true }}
          modules={[Pagination, Navigation]}
          slidesPerView="auto"
          className="!h-auto w-full"
          spaceBetween={20}
          centeredSlides
          direction="horizontal"
          wrapperClass="!flex-row"
        >
          {relatedConsultations?.slice(0, 5)?.map(consultation => {
            const { id, match_rate } = consultation
            const matchRate = getMatchRate(match_rate)

            return (
              <SwiperSlide
                key={id}
                className="!h-auto !w-[calc(100%-48px)] shrink-0 lg:!w-[500px] pb-12"
              >
                <SmartSearchCard
                  {...consultation}
                  match_rate={matchRate}
                  onClick={() => {
                    setClickedQuestion({ id, match_rate: matchRate })
                    Mixpanel.logEvent('suggestion: clicked', {
                      match_rate: matchRate,
                    })
                  }}
                />
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
      <SmartSearchBottomSheet {...contentsProps} open={!!clickedQuestion} />
    </>
  )
}
